import React, {useMemo, useState} from "react";
import CallHistory from "./CallHistory";
import CallHistoryRaw from "./CallHistoryRaw";

const SelectedCallDetails = ({
    call,
    isLoading,
    isError,
    autoRefreshMap = true,
    showAllMapMarkers = false,
    debugMode = false,
    onToggleShowAll = () => {},
    onSelectLog = () => {}
}) => {
    const [activeTab, setActiveTab] = useState("logs");

    const preparedHistory = useMemo(() => {
        if (!call?.eml?.length) {
            return [];
        }

        return call.eml.map((historyItem) => ({
            ...historyItem,
            payload: historyItem.payload?.map((payloadItem) => ({
                ...payloadItem,
                rankedLocations: [...(payloadItem.rankedLocations || [])].sort((left, right) =>
                    left.confidence > right.confidence ? -1 : 1
                )
            }))
        }));
    }, [call]);

    if (isLoading) {
        return <PanelShell>
            <div className="p-6 text-slate-500">Loading call details...</div>
        </PanelShell>;
    }

    if (isError) {
        return <PanelShell>
            <div className="p-6 text-red-500">There was some problem while loading the call details.</div>
        </PanelShell>;
    }

    if (!call) {
        return <PanelShell>
            <div className="p-6 text-slate-500">Select a call to view details.</div>
        </PanelShell>;
    }

    const parsedDate = new Date(Date.parse(call.created));
    const phone = call.phoneNumber || call.sourceIdentifier1 || "Phone No. Not Available";

    return <PanelShell>
        <div className="border-b border-slate-200 px-5 py-4">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Selected Call</div>
            <div className="mt-2 text-2xl font-semibold text-slate-700">{phone}</div>
            <div className="mt-3 space-y-1 text-sm text-slate-500">
                <div>{parsedDate.toDateString()} {parsedDate.toLocaleTimeString([], {hour: "numeric", minute: "2-digit"})}</div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-sm font-medium text-slate-600">Show All</span>
                <button
                    type="button"
                    aria-pressed={showAllMapMarkers}
                    onClick={() => onToggleShowAll(!showAllMapMarkers)}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
                        showAllMapMarkers ? "bg-blue-600" : "bg-slate-300"
                    }`}
                >
                    <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                            showAllMapMarkers ? "translate-x-6" : "translate-x-1"
                        }`}
                    />
                </button>
            </div>
            <div className="mt-3 break-all text-xs text-slate-400">{call.callUuid}</div>
        </div>
        {debugMode && <div className="flex border-b border-slate-200 px-3 pt-3">
            <TabButton active={activeTab === "logs"} onClick={() => setActiveTab("logs")}>Logs</TabButton>
            <TabButton active={activeTab === "raw"} onClick={() => setActiveTab("raw")}>Raw</TabButton>
        </div>}
        <div className="min-h-0 min-w-0 flex flex-1 flex-col overflow-hidden p-4">
            {(activeTab === "logs" || !debugMode) && <div className="min-h-0 flex-1 overflow-y-scroll pr-1">
                <CallHistory
                    callUuid={call.callUuid}
                    clickAllowed={!autoRefreshMap && !showAllMapMarkers}
                    clickHandler={onSelectLog}
                    debugMode={debugMode}
                    history={preparedHistory}
                />
            </div>}
            {activeTab === "raw" && <div className="min-h-0 flex-1 overflow-y-scroll pr-1">
                <CallHistoryRaw
                    callUuid={call.callUuid}
                    history={preparedHistory}
                />
            </div>}
        </div>
    </PanelShell>;
};

export default SelectedCallDetails;

const PanelShell = ({children}) => <section className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    {children}
</section>;

const TabButton = ({active, onClick, children}) => <button
    onClick={onClick}
    className={`rounded-t-xl px-4 py-2 text-sm font-medium ${
        active
            ? "bg-slate-100 text-blue-700"
            : "text-slate-500"
    }`}
>
    {children}
</button>;
